import { gql, useQuery } from "@apollo/client";
import React, { useMemo, useState } from "react";
import { PhoenixStyledTooltip } from "src/Components/Dumb/PhoenixStyledTooltip";
import { AppText } from "src/Components/UI";
import { FlexDiv } from "src/Components/UI/FlexDiv";
import { PhoenixIcon, PhoenixMultiSelect } from "src/Components/UI/Phoenix";
import { past } from "src/images/NewDesign";
import { OptionItem } from "src/types";
import { theme } from "src/utils/theme";
import styled from "styled-components";
import { HistoryItem } from "./HistoryItems";

const FETCH_LEAD_HISTORY = gql`
  query fetchAggregatedLeadHistory($leadId: String!, $take: Int, $skip: Int) {
    fetchAggregatedLeadHistory(lead_id: $leadId, take: $take, skip: $skip) {
      ... on LeadAssignmentHistory {
        __typename
        id
        created_at
        lead_id
        lead_assignment_origin

        old_user {
          id
          first_name
          last_name
          profile_image
        }

        sender_user {
          id
          full_name
        }
        custom_who_initiated

        receiver_user {
          id
          first_name
          last_name
          profile_image
        }
      }
      ... on LeadActivity {
        __typename
        id
        created_at
        communication_type
        channel
        title
        type
        disposition_id

        customer_number

        lead {
          id
          full_name
          associate_parent_id
          email_sub_status
        }

        user {
          id
          first_name
          last_name
          phone_number
        }

        external_transfer_number

        email_item {
          id
          subject
          html
          to_email
          from_email
          sent_at
          channel
          email_thread_id
        }

        SMSItem {
          id
          text
          sender_label
          recipient_label
        }

        conference {
          id
          duration
          recording_url_computed
          channel
        }

        related_disposition {
          id
          label
          associated_action
          computed_call_history_theme {
            completed_with_theme {
              label
              bg_color
              font_color
            }
            call_result_theme {
              label
              bg_color
              font_color
            }
          }
        }
      }
      ... on AssociationHistory {
        __typename
        id
        created_at
      }
      ... on Sale {
        __typename
        id
        created_at
        total_price
        products
        contract_duration
        payment_terms
        lead_id

        sale_items {
          id
          product {
            id
            product_title
          }
        }
      }
      ... on SequenceEntryExitLead {
        id
        created_at
        sequence {
          id
          name
        }
        exit_type
      }
    }
  }
`;

const FETCH_LEAD_CREATION = gql`
  query fetchLeadCreation($id: String!) {
    fetchLead(id: $id) {
      id
      created_at
      lead_creation_source
    }
  }
`;

interface IAggregatedLeadHistory {
  leadId: string;
  maxHeight?: string;
}

export const AggregatedLeadHistory: React.FC<IAggregatedLeadHistory> = ({ leadId, maxHeight }) => {
  const [sortBy, setSortBy] = useState<OptionItem>({ label: "Newest", value: "newest" });

  const { data, loading, error } = useQuery(FETCH_LEAD_HISTORY, {
    fetchPolicy: "network-only",
    variables: {
      leadId: leadId,
      // TODO: setup pagination via infinite scroll
      take: 30,
      skip: 0,
    },
    skip: !leadId,
  });

  const { data: dataLead } = useQuery(FETCH_LEAD_CREATION, {
    fetchPolicy: "cache-and-network",
    variables: { id: leadId },
    skip: !leadId,
  });

  const historyData = useMemo(() => {
    const filtered =
      data?.fetchAggregatedLeadHistory?.filter(
        (item: any) =>
          item.communication_type !== "Other" &&
          !(item.communication_type === "Internal" && item.title === null) &&
          !(item.communication_type === "Phone" && !item.disposition_id),
      ) || [];

    const createdEvent = { ...dataLead?.fetchLead };

    return [...filtered, createdEvent].sort((a: any, b: any) => {
      const aTime = Number(a.created_at);
      const bTime = Number(b.created_at);

      if (sortBy.value === "newest") {
        return bTime - aTime;
      } else {
        return aTime - bTime;
      }
    });
  }, [data, sortBy, dataLead]);

  return (
    <Container>
      <PhoenixStyledTooltip id="leadhistory-tooltip" />

      <FlexDiv justify="space-between" padding="0px 0px 8px 0px">
        <FlexDiv gap={8} align="center">
          <PhoenixIcon svg={past} size={12} color={theme.icon.neutral.hover} hoverColor={theme.icon.neutral.hover} />
          <AppText fontSize={10} fontWeight={500} lineHeight={14} color={theme.text.neutral.tertiary}>
            Past
          </AppText>
        </FlexDiv>
        <FlexDiv gap={8} align="center">
          <AppText fontSize={10} fontWeight={500} lineHeight={14}>
            Sort By:
          </AppText>
          <PhoenixMultiSelect
            isMulti={false}
            name="contact"
            options={[
              { label: "Newest", value: "newest" },
              { label: "Oldest", value: "oldest" },
            ]}
            placeholder="Sort"
            onChange={(e: OptionItem) => setSortBy(e)}
            value={sortBy}
            isClearable={false}
            marginBottom={false}
            error={false}
            ghostSelect
            ghostSelectAppThemeOverride="Light"
            width={64}
            height={24}
            minHeight={24}
            optionsContainerWidth={68}
            style={{ overflow: "hidden" }}
            lineHeight="10px"
            fontSize="10px"
            pointer
            menuBorder
            removeMenuPadding
            menuPortal
          />
        </FlexDiv>
      </FlexDiv>

      <Body direction="column" maxHeight={maxHeight ?? "calc(100vh - 164px)"}>
        {historyData?.map((item: any, i: number) => (
          <HistoryItem key={item?.id} data={item} zIndex={historyData.length - i} />
        ))}
      </Body>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  max-width: 860px;

  height: 100%;

  padding: 8px 16px 16px 16px;
`;

const Body = styled(FlexDiv)<{ maxHeight?: string }>`
  position: relative;
  z-index: 0;

  max-height: ${({ maxHeight }) => maxHeight ?? "100%"};
  overflow-y: auto;
`;
